/**
 * Typed DynamoDB → domain transforms.
 *
 * Extracted from app/features/project/utils.js and workspace/utils.js.
 * All DynamoDB `.S`, `.N`, `.BOOL` unwrapping happens here.
 */
import type { NormalizedItem } from '@/components/itemList/types';

// ── DynamoDB value extractors ───────────────────────────────────────

type DynamoString = { S: string } | undefined;
type DynamoNumber = { N: string } | undefined;

function s(val: DynamoString): string | undefined {
  return val?.S;
}

function n(val: DynamoNumber): string | undefined {
  return val?.N;
}

// ── Resource transform ──────────────────────────────────────────────

export function transformResource(item: Record<string, unknown>): NormalizedItem {
  // Already transformed — pass through
  if (item.id && typeof item.id === 'string') return item as unknown as NormalizedItem;

  const id = s(item.id as DynamoString) ?? '';
  const key = s(item.key as DynamoString);
  const ext = key ? key.substring(key.lastIndexOf('.') + 1) : undefined;
  const type = s(item.type as DynamoString);
  const name = s(item.name as DynamoString) || s(item.title as DynamoString);

  return {
    id,
    title: s(item.title as DynamoString) || name || '',
    name,
    itemtype: (s(item.itemtype as DynamoString) || 'resource') as NormalizedItem['itemtype'],
    type,
    created: s(item.created as DynamoString) || '',
    owner: s(item.owner as DynamoString),
    parent: s(item.parent as DynamoString),
    parentType: s(item.parentType as DynamoString),
    groupId: s(item.group as DynamoString),
    signedUrl: s(item.signedUrl as DynamoString),
    thumbnailUrl: s(item.thumbnailUrl as DynamoString) || s(item.snapshotUrl as DynamoString),
    snapshotUrl: s(item.thumbnailUrl as DynamoString) || s(item.snapshotUrl as DynamoString),
    mimeType: s(item.mimeType as DynamoString),
    size: n(item.size as DynamoNumber),
    key,
    ext,
    preview: n(item.preview as DynamoNumber) === '1',
    isRendable: type ? isRendable(type, ext) : false,
    previewImages: (item.previewImages as unknown[]) || [],
    resources: item.resources || [],
    searchField: s(item.searchField as DynamoString),
    status: n(item.status as DynamoNumber),
    image: s(item.image as DynamoString),
    breadcrumb: item.breadcrumb,
    breadcrumbs: item.breadcrumbs,
  };
}

export function transformItems(items: Record<string, unknown>[]): NormalizedItem[] {
  return items.map(transformResource);
}

// ── Folder transform ────────────────────────────────────────────────

export function transformFolder(item: Record<string, unknown>): NormalizedItem {
  if (item.id && typeof item.id === 'string') return item as unknown as NormalizedItem;

  return {
    id: s(item.id as DynamoString) ?? '',
    title: s(item.title as DynamoString) || s(item.name as DynamoString) || '',
    name: s(item.name as DynamoString),
    itemtype: (s(item.itemtype as DynamoString) || 'folder') as NormalizedItem['itemtype'],
    created: s(item.created as DynamoString) || '',
    owner: s(item.owner as DynamoString),
    parent: s(item.parent as DynamoString),
    parentType: s(item.parentType as DynamoString),
    groupId: s(item.group as DynamoString),
    previewImages: (item.previewImages as unknown[]) || [],
    resources: item.resources || [],
    image: s(item.image as DynamoString),
    searchField: s(item.searchField as DynamoString),
    status: n(item.status as DynamoNumber),
    breadcrumb: item.breadcrumb,
  };
}

export function transformFolderContent(items: Record<string, unknown>[]): NormalizedItem[] {
  return items.map((item) => {
    const itemtype = s(item.itemtype as DynamoString);
    if (itemtype === 'resource') return transformResource(item);
    return transformFolder(item);
  });
}

// ── Simple rendability check ────────────────────────────────────────

const RENDABLE_TYPES: Record<string, Set<string>> = {
  video: new Set(['mp4', 'webm', 'ogg', 'mov']),
  audio: new Set(['mp3', 'wav', 'ogg', 'aac', 'm4a', 'flac']),
  image: new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp']),
  doc: new Set(['pdf']),
};

function isRendable(type: string, ext?: string): boolean {
  const typeSet = RENDABLE_TYPES[type];
  if (!typeSet) return false;
  if (!ext) return false;
  return typeSet.has(ext.toLowerCase());
}
